import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Helmet } from "react-helmet"
import { useSearchParams } from "react-router-dom"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined"
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined"
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined"
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined"
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined"
import TableMui from "../../components/mui/TableMui"
import apiClient from "../../api/apiClient"
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from "chart.js"
import { Bar, Doughnut, Line } from "react-chartjs-2"

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
)

const palette = ["#3f83f8", "#e83f70", "#d29b12", "#12a594", "#7c5cff", "#ef6c00"]

const metricConfig = {
  clients: {
    label: "Clients Registered",
    icon: <PeopleAltOutlinedIcon />,
    color: "#e83f70",
    backgroundColor: "#ffe8f0"
  },
  samples: {
    label: "Samples Received",
    icon: <ScienceOutlinedIcon />,
    color: "#d29b12",
    backgroundColor: "#fff6dc"
  },
  reports: {
    label: "Reports Generated",
    icon: <AssessmentOutlinedIcon />,
    color: "#12a594",
    backgroundColor: "#e5fbf6"
  },
  barcodes: {
    label: "Barcodes Generated",
    icon: <QrCode2OutlinedIcon />,
    color: "#3f83f8",
    backgroundColor: "#eaf2ff"
  },
  orderFlaggedClients: {
    label: "Duplicate Orders",
    icon: <RepeatOutlinedIcon />,
    color: "#7c5cff",
    backgroundColor: "#f0edff"
  },
  flaggedCustomers: {
    label: "Flagged Customers",
    icon: <FlagOutlinedIcon />,
    color: "#ef6c00",
    backgroundColor: "#fff0df"
  }
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 12, usePointStyle: true } },
    tooltip: { mode: "nearest", intersect: true }
  },
  scales: {
    x: {
      grid: { color: "rgba(148, 163, 184, 0.14)" },
      ticks: { color: "#68717d", maxRotation: 0, autoSkip: true }
    },
    y: {
      beginAtZero: true,
      grid: { color: "rgba(148, 163, 184, 0.22)" },
      ticks: { color: "#68717d", precision: 0 }
    }
  }
}

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 12, usePointStyle: true } }
  },
  cutout: "58%"
}

const horizontalBarOptions = {
  ...chartOptions,
  indexAxis: "y",
  interaction: {
    mode: "nearest",
    axis: "y",
    intersect: false
  },
  plugins: {
    ...chartOptions.plugins,
    legend: { display: false },
    tooltip: {
      mode: "nearest",
      axis: "y",
      intersect: false
    }
  }
}

const compactBarOptions = {
  ...chartOptions,
  plugins: {
    ...chartOptions.plugins,
    legend: { display: false },
    tooltip: { mode: "nearest", intersect: true }
  }
}

const toInputDate = value => {
  if (!value) return ""
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10)
}

const formatNumber = value => Number(value || 0).toLocaleString()

const makeSingleSeriesData = (items = [], label = "Count", colors = palette) => ({
  labels: items.map(item => item.label),
  datasets: [
    {
      label,
      data: items.map(item => item.count || 0),
      backgroundColor: items.map((_, index) => colors[index % colors.length]),
      borderWidth: 0
    }
  ]
})

const analyticsTabs = [
  { key: "overview", label: "Overview" },
  { key: "operations", label: "Operations", endpoint: "/analytics/operations" },
  { key: "geographic", label: "Geography", endpoint: "/analytics/geographic" },
  { key: "reportInsights", label: "Report Insights", endpoint: "/analytics/report-insights" },
  { key: "clientQuality", label: "Client Quality", endpoint: "/analytics/client-quality" }
]

const getTabKey = value => analyticsTabs.find(item => item.key === value)?.key || "overview"

const getPeriod = value => (["day", "week", "month", "year", "all"].includes(value) ? value : "month")

const getBarcodeType = value =>
  ["all", "normal", "pp", "exp"].includes(value) ? value : "all"

function ClientAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [period, setPeriod] = useState(() => getPeriod(searchParams.get("period")))
  const [barcodeType, setBarcodeType] = useState(() => getBarcodeType(searchParams.get("barcode_type")))
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || "")
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || "")
  const [activeTab, setActiveTab] = useState(() => getTabKey(searchParams.get("tab")))
  const [analytics, setAnalytics] = useState(null)
  const [tabData, setTabData] = useState({})
  const [loading, setLoading] = useState(false)
  const [tabLoading, setTabLoading] = useState(false)
  const [error, setError] = useState("")
  const overviewLoadedRef = useRef(false)

  const getParams = useCallback(() => {
    const params = {
      period_type: period,
      barcode_type: barcodeType
    }
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    return params
  }, [barcodeType, endDate, period, startDate])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("tab", activeTab)
    params.set("period", period)
    params.set("barcode_type", barcodeType)
    if (startDate) params.set("start_date", startDate)
    if (endDate) params.set("end_date", endDate)
    setSearchParams(params, { replace: true })
  }, [activeTab, barcodeType, endDate, period, setSearchParams, startDate])

  const fetchAnalytics = useCallback(async params => {
    setLoading(true)
    setError("")
    const result = await apiClient.get("/analytics/overview", params)
    setLoading(false)

    if (!result.ok) {
      setError(result?.data?.message || "Unable to load analytics.")
      return
    }

    setAnalytics(result.data.result)
  }, [])

  const fetchTabData = useCallback(
    async tabKey => {
      const tab = analyticsTabs.find(item => item.key === tabKey)
      if (!tab?.endpoint) return

      setTabLoading(true)
      setError("")
      const result = await apiClient.get(tab.endpoint, getParams())
      setTabLoading(false)

      if (!result.ok) {
        setError(result?.data?.message || "Unable to load analytics.")
        return
      }

      setTabData(prev => ({ ...prev, [tabKey]: result.data.result }))
    },
    [getParams]
  )

  const handleApply = async () => {
    setTabData(prev => (activeTab === "overview" ? {} : { [activeTab]: prev[activeTab] }))
    if (activeTab === "overview") {
      await fetchAnalytics(getParams())
      return
    }

    await fetchTabData(activeTab)
  }

  useEffect(() => {
    if (overviewLoadedRef.current) return
    overviewLoadedRef.current = true
    fetchAnalytics(getParams())
  }, [fetchAnalytics, getParams])

  useEffect(() => {
    if (activeTab !== "overview") {
      fetchTabData(activeTab)
    }
  }, [activeTab, fetchTabData])

  const trends = useMemo(() => analytics?.trends || [], [analytics])
  const summary = analytics?.summary || {}
  const generatedAt = toInputDate(analytics?.generatedAt)

  const trendData = useMemo(
    () => ({
      labels: trends.map(item => item.formattedDate || item.date),
      datasets: [
        {
          label: "Clients",
          data: trends.map(item => item.clients || 0),
          borderColor: "#e83f70",
          backgroundColor: "rgba(232, 63, 112, 0.14)",
          tension: 0.35,
          fill: true
        },
        {
          label: "Samples",
          data: trends.map(item => item.samples || 0),
          borderColor: "#d29b12",
          backgroundColor: "rgba(210, 155, 18, 0.12)",
          tension: 0.35,
          fill: true
        },
        {
          label: "Reports",
          data: trends.map(item => item.reports || 0),
          borderColor: "#12a594",
          backgroundColor: "rgba(18, 165, 148, 0.12)",
          tension: 0.35,
          fill: true
        },
        {
          label: "Barcodes",
          data: trends.map(item => item.barcodes || 0),
          borderColor: "#3f83f8",
          backgroundColor: "rgba(63, 131, 248, 0.12)",
          tension: 0.35,
          fill: true
        }
      ]
    }),
    [trends]
  )

  const barcodeTypeData = useMemo(() => {
    const items = analytics?.barcodeTypes || []
    return {
      labels: items.map(item => item.label),
      datasets: [
        {
          data: items.map(item => item.count),
          backgroundColor: palette,
          borderWidth: 0
        }
      ]
    }
  }, [analytics])

  const petTypeData = useMemo(() => {
    const items = analytics?.petTypes || []
    return {
      labels: items.map(item => item.label),
      datasets: [
        {
          data: items.map(item => item.count),
          backgroundColor: ["#12a594", "#3f83f8", "#d29b12", "#7c5cff"],
          borderWidth: 0
        }
      ]
    }
  }, [analytics])

  const sampleStatusData = useMemo(() => {
    const items = analytics?.sampleStatuses || []
    return {
      labels: items.map(item => item.label),
      datasets: [
        {
          label: "Samples",
          data: items.map(item => item.count),
          backgroundColor: ["#d29b12", "#3f83f8", "#12a594"]
        }
      ]
    }
  }, [analytics])

  const barcodeActivationData = useMemo(() => {
    const items = analytics?.barcodeActivation || []
    return {
      labels: items.map(item => item.label),
      datasets: [
        {
          label: "Barcodes",
          data: items.map(item => item.count),
          backgroundColor: ["#12a594", "#e83f70"]
        }
      ]
    }
  }, [analytics])

  const operationsData = useMemo(() => tabData.operations || {}, [tabData.operations])
  const geographicData = useMemo(() => tabData.geographic || {}, [tabData.geographic])
  const reportInsightsData = useMemo(
    () => tabData.reportInsights || {},
    [tabData.reportInsights]
  )
  const clientQualityData = useMemo(() => tabData.clientQuality || {}, [tabData.clientQuality])

  const geographyCountryData = useMemo(
    () => makeSingleSeriesData(geographicData?.geographicBreakdown?.countries || [], "Clients"),
    [geographicData]
  )

  const geographyStateData = useMemo(
    () => makeSingleSeriesData(geographicData?.geographicBreakdown?.states || [], "Clients"),
    [geographicData]
  )

  const geographyCityData = useMemo(
    () => makeSingleSeriesData(geographicData?.geographicBreakdown?.cities || [], "Clients"),
    [geographicData]
  )

  const registrationFunnelData = useMemo(
    () =>
      makeSingleSeriesData(operationsData?.registrationFunnel || [], "Records", [
        "#3f83f8",
        "#e83f70",
        "#d29b12",
        "#12a594"
      ]),
    [operationsData]
  )

  const pipelineStatusData = useMemo(
    () =>
      makeSingleSeriesData(operationsData?.pipelineStatus || [], "Records", [
        "#ef6c00",
        "#d29b12",
        "#12a594"
      ]),
    [operationsData]
  )

  const topProductsData = useMemo(
    () => {
      const products = reportInsightsData?.topProducts || []
      return {
        labels: products.map(item => item.label),
        datasets: [
          {
            label: "Recommended",
            data: products.map(item => item.recommended || 0),
            backgroundColor: "#12a594",
            borderWidth: 0
          },
          {
            label: "Not Recommended",
            data: products.map(item => item.notRecommended || 0),
            backgroundColor: "#e83f70",
            borderWidth: 0
          }
        ]
      }
    },
    [reportInsightsData]
  )

  const energeticData = useMemo(
    () => makeSingleSeriesData(reportInsightsData?.topEnergeticImbalances || [], "Reports"),
    [reportInsightsData]
  )

  const reportPetTypeData = useMemo(
    () => makeSingleSeriesData(reportInsightsData?.reportPetTypes || [], "Reports"),
    [reportInsightsData]
  )

  const petAgeData = useMemo(
    () =>
      makeSingleSeriesData(reportInsightsData?.petAgeBuckets || [], "Pets", [
        "#12a594",
        "#3f83f8",
        "#d29b12",
        "#7c5cff",
        "#e83f70",
        "#68717d"
      ]),
    [reportInsightsData]
  )

  const rangeLabel = analytics
    ? `${toInputDate(analytics.range?.start_date)} to ${toInputDate(analytics.range?.end_date)}`
    : ""

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", pb: 6 }}>
      <Helmet>
        <title>Analytics</title>
      </Helmet>
      <Container maxWidth={false} sx={{ maxWidth: 1180, px: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#3f4a5a", mb: 0.5 }}>
            Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Operational reporting across clients, samples, reports, barcodes, duplicates, and flags.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 2.5 },
            mb: 3,
            border: "1px solid #e7eaf0",
            borderRadius: "10px",
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)"
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select value={period} label="Period" onChange={event => setPeriod(event.target.value)}>
                  <MenuItem value="day">Last 24 hours</MenuItem>
                  <MenuItem value="week">Last 7 days</MenuItem>
                  <MenuItem value="month">Last 30 days</MenuItem>
                  <MenuItem value="year">Last 12 months</MenuItem>
                  <MenuItem value="all">All time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Barcode Type</InputLabel>
                <Select
                  value={barcodeType}
                  label="Barcode Type"
                  onChange={event => setBarcodeType(event.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="pp">PP</MenuItem>
                  <MenuItem value="exp">Expedite</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start"
                type="date"
                value={startDate}
                onChange={event => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End"
                type="date"
                value={endDate}
                onChange={event => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" className="btn_bg_secondery" onClick={handleApply}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mb: 3,
            border: "1px solid #e7eaf0",
            borderRadius: "10px",
            overflow: "hidden"
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 1,
              pt: 1,
              "& .MuiTab-root": {
                borderRadius: "8px 8px 0 0",
                color: "#121826",
                minHeight: 44,
                textTransform: "none"
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#fff"
              },
              "& .MuiTabs-indicator": { display: "none" }
            }}
          >
            {analyticsTabs.map(item => (
              <Tab
                key={item.key}
                value={item.key}
                label={item.label}
                sx={{
                  backgroundColor: activeTab === item.key ? "#3E4755" : "transparent"
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {error && (
          <Paper sx={{ p: 2, mb: 3, border: "1px solid #ffd6d6", color: "#b42318" }}>{error}</Paper>
        )}

        {loading || tabLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          analytics && (
            <>
              {activeTab === "overview" && (
                <>
                  <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {Object.entries(metricConfig).map(([key, config]) => (
                      <Grid key={key} item xs={12} sm={6} md={4}>
                        <MetricCard config={config} value={summary[key]} />
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <AnalyticsPanel
                        title="Activity Trend"
                        subtitle={`${rangeLabel}${generatedAt ? ` | updated ${generatedAt}` : ""}`}
                      >
                        <Box sx={{ height: 360 }}>
                          <Line data={trendData} options={chartOptions} />
                        </Box>
                      </AnalyticsPanel>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <AnalyticsPanel title="Barcode Type Mix">
                        <Box sx={{ height: 300 }}>
                          <Doughnut data={barcodeTypeData} options={doughnutOptions} />
                        </Box>
                      </AnalyticsPanel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AnalyticsPanel title="Pet Type Mix">
                        <Box sx={{ height: 300 }}>
                          <Doughnut data={petTypeData} options={doughnutOptions} />
                        </Box>
                      </AnalyticsPanel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AnalyticsPanel title="Sample Status">
                        <Box sx={{ height: 300 }}>
                          <Bar data={sampleStatusData} options={chartOptions} />
                        </Box>
                      </AnalyticsPanel>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <AnalyticsPanel title="Barcode Activation">
                        <Box sx={{ height: 300 }}>
                          <Doughnut data={barcodeActivationData} options={doughnutOptions} />
                        </Box>
                      </AnalyticsPanel>
                    </Grid>
                  </Grid>
                </>
              )}

              {activeTab === "operations" && (
                <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Client Registration Funnel">
                    <Box sx={{ height: 300 }}>
                      <Bar data={registrationFunnelData} options={compactBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Sample Pipeline Status">
                    <Box sx={{ height: 300 }}>
                      <Bar data={pipelineStatusData} options={compactBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>
                </Grid>
              )}

              {activeTab === "geographic" && (
                <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <AnalyticsPanel title="Clients By Country">
                    <Box sx={{ height: 300 }}>
                      <Bar data={geographyCountryData} options={horizontalBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={4}>
                  <AnalyticsPanel title="Clients By State">
                    <Box sx={{ height: 300 }}>
                      <Bar data={geographyStateData} options={horizontalBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={4}>
                  <AnalyticsPanel title="Clients By City">
                    <Box sx={{ height: 300 }}>
                      <Bar data={geographyCityData} options={horizontalBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={12}>
                  <AnalyticsPanel title="All Countries">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{ label: "Country", count: "Clients" }}
                      td={geographicData?.geographicBreakdown?.allCountries || []}
                      customFields={[
                        { name: "count", data: value => <Chip size="small" label={value} /> }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={12}>
                  <AnalyticsPanel title="All States">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{ label: "State", count: "Clients" }}
                      td={geographicData?.geographicBreakdown?.allStates || []}
                      customFields={[
                        { name: "count", data: value => <Chip size="small" label={value} /> }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={12}>
                  <AnalyticsPanel title="All Cities">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{ label: "City", count: "Clients" }}
                      td={geographicData?.geographicBreakdown?.allCities || []}
                      customFields={[
                        { name: "count", data: value => <Chip size="small" label={value} /> }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>
                </Grid>
              )}

              {activeTab === "reportInsights" && (
                <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Product Recommendations" subtitle="Uses the selected date range">
                    <Box sx={{ height: 360 }}>
                      <Bar data={topProductsData} options={horizontalBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Energetic Imbalances">
                    <Box sx={{ height: 360 }}>
                      <Bar data={energeticData} options={horizontalBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12}>
                  <AnalyticsPanel title="Product Recommendation Counts">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{
                        label: "Product",
                        recommended: "Recommended",
                        notRecommended: "Not Recommended",
                        total: "Total"
                      }}
                      td={reportInsightsData.topProducts || []}
                      customFields={[
                        {
                          name: "recommended",
                          data: value => <Chip size="small" color="success" label={value} />
                        },
                        {
                          name: "notRecommended",
                          data: value => <Chip size="small" color="error" label={value} />
                        },
                        { name: "total", data: value => <Chip size="small" label={value} /> }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Pet Types In Reports">
                    <Box sx={{ height: 300 }}>
                      <Doughnut data={reportPetTypeData} options={doughnutOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={6}>
                  <AnalyticsPanel title="Pet Age Buckets">
                    <Box sx={{ height: 300 }}>
                      <Bar data={petAgeData} options={compactBarOptions} />
                    </Box>
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12}>
                  <AnalyticsPanel title="Most Common Intolerance Items">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{
                        label: "Item",
                        count: "Total",
                        high: "High",
                        medium: "Medium",
                        noReaction: "No Reaction"
                      }}
                      td={reportInsightsData.topIntoleranceItems || []}
                      customFields={[
                        { name: "count", data: value => <Chip size="small" label={value} /> },
                        { name: "high", data: value => <Chip size="small" color="error" label={value} /> },
                        { name: "medium", data: value => <Chip size="small" color="warning" label={value} /> },
                        { name: "noReaction", data: value => <Chip size="small" label={value} /> }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>
                </Grid>
              )}

              {activeTab === "clientQuality" && (
                <Grid container spacing={2.5}>
                <Grid item xs={12} md={12}>
                  <AnalyticsPanel title="Repeated Clients">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{
                        name: "Client",
                        email: "Email",
                        phone_no: "Phone",
                        registrations: "Registrations",
                        barcodes: "Barcodes"
                      }}
                      td={clientQualityData.repeatedClients || []}
                      customFields={[
                        { name: "registrations", data: value => <Chip size="small" label={value} /> },
                        { name: "barcodes", data: value => (value || []).join(", ") }
                      ]}
                    />
                  </AnalyticsPanel>
                </Grid>

                <Grid item xs={12} md={12}>
                  <AnalyticsPanel title="Duplicate And Flagged Clients">
                    <TableMui
                      styleTableContainer={{ maxHeight: 360 }}
                      styleTableTh={tableHeaderSx}
                      th={{
                        name: "Client",
                        email: "Email",
                        pet_name: "Pet",
                        barcode: "Barcode",
                        reason: "Reason"
                      }}
                      td={clientQualityData.duplicateClients || []}
                    />
                  </AnalyticsPanel>
                </Grid>
                </Grid>
              )}
            </>
          )
        )}
      </Container>
    </Box>
  )
}

const tableHeaderSx = {
  color: "white",
  backgroundColor: "#3E4755",
  fontWeight: "bold",
  whiteSpace: "nowrap"
}

function MetricCard({ config, value }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "10px",
        border: "1px solid #edf0f4",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)"
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.75, p: 2.5 }}>
        <Box
          sx={{
            width: 54,
            height: 54,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: config.backgroundColor,
            color: config.color,
            borderRadius: "10px",
            fontSize: 30,
            flexShrink: 0
          }}
        >
          {config.icon}
        </Box>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
            {config.label}
          </Typography>
          <Typography variant="h4" sx={{ color: "#121826", fontWeight: 700, lineHeight: 1.15 }}>
            {formatNumber(value)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

function AnalyticsPanel({ title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "10px",
        border: "1px solid #edf0f4",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
        height: "100%"
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: "#121826", fontWeight: 700 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  )
}

export default ClientAnalytics
